import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PriceChart } from "./PriceChart";
import { MarketStats } from "./MarketStats";
import { OrderBook } from "./OrderBook";
import { SwapPanel } from "./SwapPanel";
import { AvailableBalance } from "./AvailableBalance";
import { ActivityPanel } from "./ActivityPanel";

import { useGummiwormContext } from "@/context/GummiwormContext";
import type { Market } from "@module-bindings/types";
import {
  assetIdKey,
  collectTokens,
  findMarketForPair,
} from "@/lib/marketUtils";

type MobileTab = "chart" | "book" | "orders";

interface TradingViewProps {
  onDeposit: () => void;
  depositDisabled?: boolean;
}

export const TradingView: FC<TradingViewProps> = ({ onDeposit, depositDisabled }) => {
  const [mobileTab, setMobileTab] = useState<MobileTab | null>(null);
  const { markets } = useGummiwormContext();

  const allTokens = useMemo(() => collectTokens(markets), [markets]);
  const tokensByKey = useMemo(
    () => new Map(allTokens.map((t) => [t.key, t])),
    [allTokens],
  );

  // Read URL params once at mount
  const [initialUrlSell] = useState(() => new URLSearchParams(window.location.search).get("sell"));
  const [initialUrlBuy] = useState(() => new URLSearchParams(window.location.search).get("buy"));

  const [sellKey, setSellKey] = useState<string | null>(null);
  const [buyKey, setBuyKey] = useState<string | null>(null);
  const initialized = useRef(false);

  // Initialize sell/buy from URL or fall back to first market
  useEffect(() => {
    if (initialized.current || allTokens.length === 0) return;
    initialized.current = true;

    // Try URL params first
    if (initialUrlSell || initialUrlBuy) {
      const sell = initialUrlSell ? allTokens.find((t) => t.label === initialUrlSell) : null;
      const buy = initialUrlBuy ? allTokens.find((t) => t.label === initialUrlBuy) : null;
      if (sell || buy) {
        if (sell) setSellKey(sell.key);
        if (buy) setBuyKey(buy.key);
        return;
      }
    }

    // Fall back to first market
    if (markets.length > 0) {
      const m = markets[0];
      setSellKey(assetIdKey(m.tradingPair.base));
      setBuyKey(assetIdKey(m.tradingPair.quote));
    }
  }, [allTokens, markets, initialUrlSell, initialUrlBuy]);

  // Resolve matched market from current token selections
  const matched = useMemo(
    () => (sellKey && buyKey ? findMarketForPair(sellKey, buyKey, markets) : null),
    [sellKey, buyKey, markets],
  );
  const selectedMarket = matched?.market ?? null;

  // Write sell/buy labels to URL when they change
  useEffect(() => {
    const sellLabel = sellKey ? tokensByKey.get(sellKey)?.label : null;
    const buyLabel = buyKey ? tokensByKey.get(buyKey)?.label : null;
    const params = new URLSearchParams(window.location.search);
    if (sellLabel) params.set("sell", sellLabel); else params.delete("sell");
    if (buyLabel) params.set("buy", buyLabel); else params.delete("buy");
    const qs = params.toString();
    const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    window.history.replaceState(null, "", url);
  }, [sellKey, buyKey, tokensByKey]);

  const handleSelectMarket = (market: Market | null) => {
    if (!market) return;
    setSellKey(assetIdKey(market.tradingPair.base));
    setBuyKey(assetIdKey(market.tradingPair.quote));
  };

  const [orderType, setOrderType] = useState<"market" | "limit">("market");
  const [limitPrice, setLimitPrice] = useState("");

  const handleSelectPrice = useCallback((price: number) => {
    setOrderType("limit");
    setLimitPrice(price.toFixed(6).replace(/0+$/, "").replace(/\.$/, ""));
  }, []);

  const swapProps = {
    selectedMarket,
    markets,
    onSelectMarket: handleSelectMarket,
    sellKey,
    buyKey,
    onSellKeyChange: setSellKey,
    onBuyKeyChange: setBuyKey,
    orderType,
    onOrderTypeChange: setOrderType,
    limitPrice,
    onLimitPriceChange: setLimitPrice,
  };

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col gap-1">
      {/* === Desktop Layout === */}
      <div className="hidden min-h-0 flex-1 gap-1 lg:grid lg:grid-cols-[420px_minmax(0,1fr)]">
        {/* Left column: Swap + Balance */}
        <div className="flex min-w-0 flex-col gap-1">
          <SwapPanel {...swapProps} />
          <AvailableBalance />
        </div>

        {/* Right column: Chart + Book + Activity */}
        <div className="flex min-h-0 min-w-0 flex-col gap-1">
          <div className="grid min-h-0 min-w-0 flex-1 gap-1 lg:grid-cols-[minmax(0,1fr)_340px]">
            <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-1">
              <PriceChart selectedMarket={selectedMarket} markets={markets} onSelectMarket={handleSelectMarket} />
              <MarketStats />
            </div>
            <div className="min-h-0 min-w-0">
              <OrderBook selectedMarket={selectedMarket} onSelectPrice={handleSelectPrice} />
            </div>
          </div>
          <ActivityPanel selectedMarket={selectedMarket} />
        </div>
      </div>

      {/* === Mobile Layout === */}
      <div className="flex flex-col gap-1 lg:hidden">
        {/* Compact chart */}
        <PriceChart compact selectedMarket={selectedMarket} markets={markets} onSelectMarket={handleSelectMarket} />

        {/* Swap panel (always visible on mobile) */}
        <SwapPanel {...swapProps} />

        {/* Balance */}
        <AvailableBalance />

        {/* Deposit button */}
        <button
          className="sr-btn-primary"
          onClick={onDeposit}
          disabled={depositDisabled}
        >
          Deposit
        </button>

        {/* Mobile expandable sections */}
        {mobileTab === "chart" && (
          <div className="flex flex-col gap-1">
            <PriceChart selectedMarket={selectedMarket} markets={markets} onSelectMarket={handleSelectMarket} />
            <MarketStats />
          </div>
        )}
        {mobileTab === "book" && <OrderBook selectedMarket={selectedMarket} onSelectPrice={handleSelectPrice} />}
        {mobileTab === "orders" && <ActivityPanel selectedMarket={selectedMarket} />}

        {/* Bottom tab bar */}
        <div className="flex rounded-xl overflow-hidden bg-[#23141A]">
          {([
            ["chart", "Chart"],
            ["book", "Book"],
            ["orders", "Orders"],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setMobileTab(mobileTab === key ? null : key)}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                mobileTab === key
                  ? "text-sr-lemon-muted bg-sr-espresso"
                  : "text-sr-neutral-500 hover:text-sr-neutral-300"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
